import { useState, useEffect, useContext } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { AuthContext } from '../App';

const MessageModal = ({ isOpen, onClose, targetUser, onSuccess }) => {
  const { user } = useContext(AuthContext);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [conversation, setConversation] = useState(null);

  useEffect(() => {
    if (isOpen && targetUser) {
      // Reset state when modal opens
      setMessage('');
      setConversation(null);
      createOrGetConversation();
    }
  }, [isOpen, targetUser]);

  const createOrGetConversation = async () => {
    if (!targetUser) return;
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.post(`${backendUrl}/api/messages/direct/${targetUser.id}`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setConversation(response.data.conversation);
    } catch (error) {
      console.error('Error creating conversation:', error);
    }
  };

  const sendMessage = async () => {
    if (!message.trim() || !conversation || sending) return;
    
    setSending(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      await axios.post(`${backendUrl}/api/messages/${conversation.id}/send`, {
        content: message.trim(),
        message_type: 'text'
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Success - close modal and show success message
      if (onSuccess) {
        onSuccess(`Message sent to ${targetUser.full_name}!`);
      }
      onClose();
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!isOpen || !targetUser) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />
        
        {/* Modal */}
        <div className="relative z-[10000] transform overflow-hidden rounded-2xl bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-teal-500 text-white rounded-full flex items-center justify-center font-semibold">
                {targetUser.full_name.charAt(0)}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  💬 Message {targetUser.full_name}
                </h3>
                <p className="text-sm text-gray-500">
                  {targetUser.company_name && `${targetUser.company_name} • `}
                  {targetUser.location}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-4">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Message
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message here..."
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                autoFocus
              />
              <p className="text-xs text-gray-500 mt-1">
                Press Enter to send, or Shift+Enter for new line
              </p>
            </div>

            {/* Quick Templates */}
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Quick Templates:</p>
              <div className="space-y-2">
                <button
                  onClick={() => setMessage("Hi! I'd like to connect and explore potential business opportunities together.")}
                  className="w-full text-left px-3 py-2 text-sm bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  👋 Business Introduction
                </button>
                <button
                  onClick={() => setMessage("Hello! I saw your profile and thought we might have mutual business interests. Would you like to chat?")}
                  className="w-full text-left px-3 py-2 text-sm bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  🤝 Connection Request
                </button>
                <button
                  onClick={() => setMessage("Hi! I'm interested in learning more about your services. Could we discuss potential collaboration?")}
                  className="w-full text-left px-3 py-2 text-sm bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  💼 Service Inquiry
                </button>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex space-x-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={sendMessage}
              disabled={!message.trim() || sending || !conversation}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {sending ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Sending...
                </>
              ) : (
                <>
                  📤 Send Message
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Render modal via portal to document body
  return createPortal(modalContent, document.body);
};

export default MessageModal;